// components/CreatePaymentAgreementModal.tsx
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { addDays, addMonths, addWeeks, format, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
  useCreatePaymentAgreement,
  useInstallmentFrequency,
  usePreferredDayOfMonth,
  useWeekdayOptions,
} from "../../hooks";
import {
  usePaymentChannelChoices,
  usePaymentMode,
} from "@features/cx/paymentsv2/hooks";

interface CreatePaymentAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalOutstanding: number;
  latestBillAmount?: number;
  consumerId?: number;
  remoteBillId?: number;
}

const generateDaysOfMonthOptions = () => {
  const options = [];
  for (let day = 1; day <= 29; day++) {
    options.push({ label: day.toString(), value: day });
  }
  return options;
};

// Helper to convert snake_case to camelCase
const snakeToCamel = (str: string): string => {
  if (!str) return "";
  return str.replace(/([-_][a-z])/gi, ($1) =>
    $1.toUpperCase().replace("-", "").replace("_", ""),
  );
};

export function CreatePaymentAgreementModal({
  isOpen,
  onClose,
  totalOutstanding,
  latestBillAmount = 0,
  consumerId,
  remoteBillId,
}: CreatePaymentAgreementModalProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const createPaymentAgreement = useCreatePaymentAgreement(
    Number(remoteUtilityId),
  );

  // Fetch options from hooks
  const { data: frequencyData } = useInstallmentFrequency();
  const { data: weekdayOptionsData } = useWeekdayOptions();
  const { data: monthOptionsData } = usePreferredDayOfMonth();
  const { data: paymentChannelData } = usePaymentChannelChoices(
    Number(remoteUtilityId),
  );
  const { data: paymentModesData } = usePaymentMode(Number(remoteUtilityId));

  // Map frequency options from hook
  const frequencyOptions = useMemo(() => {
    return (
      frequencyData?.map((item) => ({
        value: item.key,
        label: item.value,
      })) || []
    );
  }, [frequencyData]);

  const monthOptions = useMemo(() => {
    return (
      monthOptionsData?.map((item) => ({
        value: item.key,
        label: item.value,
      })) || []
    );
  }, [monthOptionsData]);

  const weekdayOptions = useMemo(() => {
    return (
      weekdayOptionsData?.map((item) => ({
        value: item.key,
        label: item.value,
      })) || []
    );
  }, [weekdayOptionsData]);

  const paymentChannelOptions = useMemo(() => {
    const channels = Array.isArray(paymentChannelData)
      ? paymentChannelData
      : paymentChannelData?.result || paymentChannelData?.data || [];
    return channels.map((channel: any) => ({
      value: channel.code || channel.key || String(channel.id),
      label: channel.name || channel.value || channel.label,
    }));
  }, [paymentChannelData]);

  const paymentModeList = useMemo(() => {
    return Array.isArray(paymentModesData)
      ? paymentModesData
      : paymentModesData?.result || paymentModesData?.data || [];
  }, [paymentModesData]);

  const paymentModeOptions = useMemo(() => {
    return paymentModeList.map((mode: any) => ({
      value: mode.code || mode.key || String(mode.id),
      label: mode.name || mode.value || mode.label,
    }));
  }, [paymentModeList]);

  // Create form instance
  const form = FormService.createForm({
    fields: [],
    mode: "onSubmit",
  });

  // Watch form values for calculations and dynamic fields
  const formValues = form.watch();
  const excludeLatestBill = formValues.excludeLatestBill || false;
  const downPayment = Number(formValues.downPayment) || 0;
  const numberOfInstallments = Number(formValues.numberOfInstallments) || 1;
  const installmentStartDate = formValues.installmentStartDate || "";
  const installmentFrequency = formValues.installmentFrequency || "";
  const selectedPaymentModeCode = formValues.paymentMode || "";

  // Resolve current selected payment mode object from API response
  const currentModeObj = useMemo(() => {
    return paymentModeList.find(
      (m: any) => (m.code || m.key || String(m.id)) === selectedPaymentModeCode,
    );
  }, [paymentModeList, selectedPaymentModeCode]);

  // Dynamic custom fields generated directly from API response custom_fields
  const dynamicCustomFields: FormField[] = useMemo(() => {
    if (
      !currentModeObj ||
      !Array.isArray(
        currentModeObj.custom_fields || currentModeObj.customFields,
      )
    ) {
      return [];
    }

    const customFieldsRaw =
      currentModeObj.custom_fields || currentModeObj.customFields || [];

    return customFieldsRaw.map((field: any) => {
      const camelKey = snakeToCamel(field.key);
      let fieldType: string = "text";
      if (field.field_type === "number" || field.fieldType === "number") {
        fieldType = "number";
      } else if (
        field.field_type === "date" ||
        field.fieldType === "date" ||
        field.field_type === "datepicker" ||
        field.fieldType === "datepicker"
      ) {
        fieldType = "datepicker";
      } else if (
        field.field_type === "select" ||
        field.fieldType === "select"
      ) {
        fieldType = "select";
      }

      const isDate = fieldType === "datepicker";

      return {
        name: camelKey,
        label: field.label || field.name || camelKey,
        type: fieldType,
        required: false,
        placeholder: `Enter ${field.label || camelKey}`,
        classes: FIELD_CLASSES,
        ...(isDate ? { defaultValue: format(new Date(), "yyyy-MM-dd") } : {}),
      } as FormField;
    });
  }, [currentModeObj]);

  // Sync downPayment value ONLY into amount field (not cash received / paymentAmount)
  useEffect(() => {
    form.setValue("amount", downPayment);
  }, [downPayment, selectedPaymentModeCode, form]);

  // Form fields configuration
  const formFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [
      {
        name: "excludeLatestBill",
        type: "switch",
        label: "Exclude Latest Bill",
        required: false,
        defaultValue: false,
        helperText: `Exclude the latest bill (${currency} ${latestBillAmount}) from the payment plan`,
        classes: FIELD_CLASSES,
      },
      {
        name: "downPayment",
        type: "number",
        label: `Down Payment (${currency})`,
        required: true,
        placeholder: "Enter down payment amount",
        min: 0,
        step: 0.01,
        classes: FIELD_CLASSES,
        helperText:
          "Amount to be paid upfront, reducing the total agreement amount",
      },
      {
        name: "paymentChannel",
        type: "select",
        label: "Payment Channel",
        required: true,
        placeholder: "Select channel",
        options: paymentChannelOptions,
        defaultValue: "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Select how the payment was received",
        helperText: "Channel used for down payment",
      },
      {
        name: "paymentMode",
        type: "select",
        label: "Payment Mode",
        required: true,
        placeholder: "Select mode",
        options: paymentModeOptions,
        defaultValue: "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Select the mode of payment",
        helperText: "Mode used for down payment",
      },
    ];

    // Append all dynamic custom fields directly from response custom_fields
    if (dynamicCustomFields.length > 0) {
      fields.push(...dynamicCustomFields);
    }

    // Agreement fields
    fields.push(
      {
        name: "numberOfInstallments",
        type: "number",
        label: "Number of Installments",
        required: true,
        placeholder: "Enter number of installments",
        min: 1,
        max: 60,
        defaultValue: 1,
        classes: FIELD_CLASSES,
        helperText: "Total number of installment payments",
      },
      {
        name: "installmentStartDate",
        type: "datepicker",
        label: "Installment Start Date",
        required: true,
        defaultValue: "",
        allowPast: false,
        allowFuture: true,
        classes: FIELD_CLASSES,
        helperText:
          "First installment due date - only present or future dates allowed",
      },
      {
        name: "installmentFrequency",
        type: "select",
        label: "Installment Frequency",
        required: true,
        placeholder: "Select frequency",
        options: frequencyOptions,
        defaultValue: "",
        classes: FIELD_CLASSES,
        helperText: "How often installments are due",
      },
      {
        name: "preferredDayOfMonth",
        type: "select",
        label: "Preferred Day of the Month",
        placeholder: "Select day",
        options: generateDaysOfMonthOptions(),
        defaultValue: "",
        classes: FIELD_CLASSES,
        helperText: "Day of the month for installment payments",
        showWhen: (formVals) => formVals.installmentFrequency === "4",
      },
      {
        name: "preferredWeekday",
        type: "select",
        label: "Preferred Weekday",
        placeholder: "Select weekday",
        options: weekdayOptions,
        defaultValue: "",
        classes: FIELD_CLASSES,
        helperText: "Day of the week for installment payments",
        showWhen: (formVals) => formVals.installmentFrequency === "1",
      },
    );

    return fields;
  }, [
    currency,
    latestBillAmount,
    frequencyOptions,
    monthOptions,
    weekdayOptions,
    paymentChannelOptions,
    paymentModeOptions,
    dynamicCustomFields,
  ]);

  // Calculate agreement summary values
  const agreementSummary = useMemo(() => {
    const lessLatestBill = excludeLatestBill ? latestBillAmount : 0;
    const lessDownPayment = downPayment || 0;
    const agreementAmount = totalOutstanding - lessLatestBill - lessDownPayment;
    const installmentAmount =
      numberOfInstallments > 0 ? agreementAmount / numberOfInstallments : 0;

    // Calculate end date
    let endDate = "";
    if (
      installmentStartDate &&
      installmentFrequency &&
      numberOfInstallments > 0
    ) {
      try {
        const startDate = parse(installmentStartDate, "yyyy-MM-dd", new Date());
        let calculatedEndDate = startDate;

        // Resolve frequency label for calculation
        const frequencyLabel =
          frequencyData?.find(
            (f) => String(f.key) === String(installmentFrequency),
          )?.value || "";

        for (let i = 1; i < numberOfInstallments; i++) {
          switch (frequencyLabel) {
            case "Daily":
              calculatedEndDate = addDays(calculatedEndDate, 1);
              break;
            case "Weekly":
              calculatedEndDate = addWeeks(calculatedEndDate, 1);
              break;
            case "Biweekly":
              calculatedEndDate = addWeeks(calculatedEndDate, 2);
              break;
            case "Monthly":
              calculatedEndDate = addMonths(calculatedEndDate, 1);
              break;
            case "Bimonthly":
              calculatedEndDate = addMonths(calculatedEndDate, 2);
              break;
            case "Quarterly":
              calculatedEndDate = addMonths(calculatedEndDate, 3);
              break;
          }
        }

        endDate = format(calculatedEndDate, "yyyy-MM-dd");
      } catch (error) {
        console.error("Error calculating end date:", error);
      }
    }

    return {
      totalOutstanding,
      lessLatestBill,
      lessDownPayment,
      agreementAmount: agreementAmount > 0 ? agreementAmount : 0,
      installmentAmount: installmentAmount > 0 ? installmentAmount : 0,
      endDate,
    };
  }, [
    totalOutstanding,
    latestBillAmount,
    excludeLatestBill,
    downPayment,
    numberOfInstallments,
    installmentStartDate,
    installmentFrequency,
    frequencyData,
  ]);

  // Handle form submission using camelCase keys throughout payload
  const handleSubmit = async (data: any) => {
    try {
      setIsCreating(true);

      const agreementPayload: any = {
        remoteUtilityId: Number(remoteUtilityId),
        remoteBillId: Number(remoteBillId),
        consumer: Number(consumerId),
        totalAgreementAmount:
          agreementSummary.agreementAmount + agreementSummary.lessDownPayment,
        downPayment: agreementSummary.lessDownPayment,
        amount: agreementSummary.lessDownPayment,
        paymentChannel: data.paymentChannel,
        paymentMode: data.paymentMode,
        noOfInstallments: Number(data.numberOfInstallments),
        monthlyPayment: agreementSummary.installmentAmount,
        installmentFrequency: Number(data.installmentFrequency),
        installmentStartDate: data.installmentStartDate,
        endDate: agreementSummary.endDate,
      };

      // Dynamically attach ALL custom fields from selected payment mode in camelCase
      const customFieldsRaw =
        currentModeObj?.custom_fields || currentModeObj?.customFields || [];
      customFieldsRaw.forEach((field: any) => {
        if (field && field.key) {
          const camelKey = snakeToCamel(field.key);
          if (
            data[camelKey] !== undefined &&
            data[camelKey] !== null &&
            data[camelKey] !== ""
          ) {
            agreementPayload[camelKey] =
              field.field_type === "number" || field.fieldType === "number"
                ? Number(data[camelKey])
                : data[camelKey];
          }
        }
      });

      // Ensure amount strictly matches downPayment value
      agreementPayload.amount = agreementSummary.lessDownPayment;

      // Add preferredWeekday or preferredDayOfMonth depending on frequency selected
      if (String(data.installmentFrequency) === "1") {
        // Weekly
        agreementPayload.preferredWeekday = Number(data.preferredWeekday);
      } else if (String(data.installmentFrequency) === "4") {
        // Monthly
        agreementPayload.preferredDayOfMonth = Number(data.preferredDayOfMonth);
      }

      createPaymentAgreement.mutate(agreementPayload);
      handleClose();
    } catch (error) {
      console.error("Error creating agreement:", error);
      toast({
        title: "Error",
        description: "Failed to create payment agreement. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const onSubmitAgreement = async () => {
    const isValid = await FormService.validateForm(form);

    if (!isValid) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const formDataValues = form.getValues();
    handleSubmit(formDataValues);
  };

  // Handle modal close
  const handleClose = () => {
    form.reset();
    onClose();
  };

  // Get frequency label for display
  const getFrequencyLabel = (key: string) => {
    return (
      frequencyData?.find((item: any) => String(item.key) === String(key))
        ?.value || key
    );
  };

  // Summary section component
  const AgreementSummarySection = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 space-y-3">
      <h4 className="font-semibold text-blue-900 text-sm mb-3">
        Agreement Summary
      </h4>

      <div className="space-y-2">
        {/* Total Outstanding */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-700">Total Outstanding</span>
          <span className="font-medium text-gray-900">
            {currency} {agreementSummary.totalOutstanding}
          </span>
        </div>

        {/* Less Down Payment */}
        {agreementSummary.lessDownPayment > 0 && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-700">Less: Down Payment</span>
            <span className="font-medium text-red-600">
              - {currency} {agreementSummary.lessDownPayment}
            </span>
          </div>
        )}

        {/* Less Latest Bill */}
        {agreementSummary.lessLatestBill > 0 && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-700">Less: Latest Bill</span>
            <span className="font-medium text-red-600">
              - {currency} {agreementSummary.lessLatestBill}
            </span>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-blue-200 my-2"></div>

        {/* Agreement Amount */}
        <div className="flex justify-between items-center text-sm pt-2">
          <span className="font-semibold text-blue-900">Agreement Amount</span>
          <span className="font-bold text-blue-700 text-base">
            {currency} {agreementSummary.agreementAmount}
          </span>
        </div>

        {/* Installment Amount */}
        {numberOfInstallments > 0 && installmentFrequency && (
          <div className="flex justify-between items-center text-sm bg-white p-3 rounded border border-blue-200">
            <span className="text-gray-700">
              Installment Amount ({getFrequencyLabel(installmentFrequency)})
            </span>
            <span className="font-bold text-green-700">
              {currency} {agreementSummary.installmentAmount}
            </span>
          </div>
        )}

        {/* End Date */}
        {agreementSummary.endDate && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-700">Agreement End Date</span>
            <span className="font-medium text-gray-900">
              {format(
                parse(agreementSummary.endDate, "yyyy-MM-dd", new Date()),
                "MMM dd, yyyy",
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Create Payment Agreement
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Set up a payment plan for the outstanding balance
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Form Section */}
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              layout: "vertical",
              spacing: "medium",
              showSubmitButton: false,
              showResetButton: false,
            }}
          />

          {/* Agreement Summary Section */}
          <AgreementSummarySection />

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={onSubmitAgreement} isLoading={isCreating}>
              Create Agreement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
